/**
 * [관리자] AI 분석 토큰 사용량 통계
 * 전체 요약 + 사용자별 집계 + 최근 30일 추이
 */
import React, { useState, useEffect } from 'react';
import { apiBaseUrl } from '../config/runtime';

interface UserStat {
  user_id: number;
  nickname: string;
  email: string;
  requests: number;
  apiCalls: number;
  cacheHits: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostKrw: number;
  lastUsed: string;
}

const AdminTokenStats: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('[AdminTokenStats] 통계 로드');
    const token = localStorage.getItem('token');
    fetch(apiBaseUrl('/chatbot/admin/usage-stats'), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) setData(d.data);
        else setError(d.message || '통계 로드 실패');
      })
      .catch(() => setError('네트워크 오류'));
  }, []);

  if (error) return <div style={{ padding: 12, color: '#dc3545' }}>{error}</div>;
  if (!data) return <div style={{ padding: 12, color: '#666' }}>통계 로딩중...</div>;

  const { summary, byUser } = data;
  const fmt = (n: number | string) => Number(n || 0).toLocaleString();

  const summaryCards = [
    { label: '총 요청', value: fmt(summary.totalRequests) },
    { label: 'API 호출 (신규 분석)', value: fmt(summary.apiCalls) },
    { label: '캐시 적중', value: fmt(summary.cacheHits) },
    { label: '입력 토큰', value: fmt(summary.inputTokens) },
    { label: '출력 토큰', value: fmt(summary.outputTokens) },
    { label: '예상 API 비용', value: `약 ${fmt(summary.estimatedCostKrw)}원` },
  ];

  const th: React.CSSProperties = {
    padding: '8px 10px', textAlign: 'left', fontSize: 13,
    borderBottom: '2px solid #e9ecef', color: '#495057', whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = {
    padding: '8px 10px', fontSize: 13, borderBottom: '1px solid #f1f3f5', whiteSpace: 'nowrap',
  };

  return (
    <div>
      {/* 요약 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
        {summaryCards.map((c, i) => (
          <div key={i} style={{ padding: '14px', backgroundColor: '#f8f9fa', borderRadius: 10, textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#212529' }}>{c.value}</div>
            <div style={{ fontSize: 12, color: '#868e96', marginTop: 4 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* 사용자별 표 */}
      {byUser.length === 0 ? (
        <div style={{ padding: 20, color: '#666', backgroundColor: '#f8f9fa', borderRadius: 10, textAlign: 'center' }}>
          아직 사용 기록이 없습니다.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', backgroundColor: '#fff', borderRadius: 10, border: '1px solid #e9ecef' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>사용자</th>
                <th style={th}>총 요청</th>
                <th style={th}>API 호출</th>
                <th style={th}>캐시</th>
                <th style={th}>입력 토큰</th>
                <th style={th}>출력 토큰</th>
                <th style={th}>예상 비용</th>
                <th style={th}>마지막 사용</th>
              </tr>
            </thead>
            <tbody>
              {byUser.map((u: UserStat) => (
                <tr key={u.user_id}>
                  <td style={td}><strong>{u.nickname}</strong><br /><span style={{ color: '#adb5bd', fontSize: 11 }}>{u.email}</span></td>
                  <td style={td}>{fmt(u.requests)}</td>
                  <td style={td}>{fmt(u.apiCalls)}</td>
                  <td style={td}>{fmt(u.cacheHits)}</td>
                  <td style={td}>{fmt(u.inputTokens)}</td>
                  <td style={td}>{fmt(u.outputTokens)}</td>
                  <td style={td}>{fmt(u.estimatedCostKrw)}원</td>
                  <td style={td}>{u.lastUsed ? new Date(u.lastUsed).toLocaleDateString('ko-KR') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminTokenStats;
